import { supabase } from '../lib/supabase';

export type FilterType = 'name' | 'description' | 'photo' | 'tags' | 'brand' | 'all';
export type FilterCategory = 'inappropriate' | 'illegal' | 'spam' | 'prohibited' | 'custom';
export type ActionType = 'block' | 'flag';

export interface FilterSetting {
  id: string;
  filter_type: FilterType;
  filter_category: FilterCategory;
  keyword: string;
  case_sensitive: boolean;
  is_active: boolean;
  action_type: ActionType;
  description?: string;
  priority: number;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface CreateFilterSettingData {
  filter_type: FilterType;
  filter_category: FilterCategory;
  keyword: string;
  case_sensitive?: boolean;
  is_active?: boolean;
  action_type?: ActionType;
  description?: string;
  priority?: number;
}

export interface UpdateFilterSettingData extends Partial<CreateFilterSettingData> {}

export class FilterSettingsService {
  /**
   * Get all filter settings
   */
  static async getFilterSettings(): Promise<FilterSetting[]> {
    try {
      const { data, error } = await supabase
        .from('filter_settings')
        .select('*')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching filter settings:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getFilterSettings:', error);
      throw error;
    }
  }

  /**
   * Get active filter settings for a specific filter type
   */
  static async getActiveFiltersForType(filterType: FilterType): Promise<FilterSetting[]> {
    try {
      const { data, error } = await supabase
        .from('filter_settings')
        .select('*')
        .eq('is_active', true)
        .or(`filter_type.eq.${filterType},filter_type.eq.all`)
        .order('priority', { ascending: false });

      if (error) {
        console.error('Error fetching active filters:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getActiveFiltersForType:', error);
      throw error;
    }
  }

  /**
   * Create a new filter setting
   */
  static async createFilterSetting(
    filterData: CreateFilterSettingData
  ): Promise<FilterSetting> {
    try {
      // Get current user for created_by
      const { data: { user } } = await supabase.auth.getUser();
      
      const insertData = {
        ...filterData,
        case_sensitive: filterData.case_sensitive ?? false,
        is_active: filterData.is_active ?? true,
        action_type: filterData.action_type ?? 'block',
        priority: filterData.priority ?? 0,
        created_by: user?.id,
        updated_by: user?.id,
      };

      const { data, error } = await supabase
        .from('filter_settings')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('Error creating filter setting:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in createFilterSetting:', error);
      throw error;
    }
  }

  /**
   * Update an existing filter setting
   */
  static async updateFilterSetting(
    id: string,
    updates: UpdateFilterSettingData
  ): Promise<FilterSetting> {
    try {
      // Get current user for updated_by
      const { data: { user } } = await supabase.auth.getUser();
      
      const updateData = {
        ...updates,
        updated_by: user?.id,
      };

      const { data, error } = await supabase
        .from('filter_settings')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating filter setting:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in updateFilterSetting:', error);
      throw error;
    }
  }

  /**
   * Delete a filter setting
   */
  static async deleteFilterSetting(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('filter_settings')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting filter setting:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deleteFilterSetting:', error);
      throw error;
    }
  }

  /**
   * Toggle active status of a filter setting
   */
  static async toggleFilterActive(id: string, isActive: boolean): Promise<FilterSetting> {
    return this.updateFilterSetting(id, { is_active: isActive });
  }

  /**
   * Check if content violates any active filters
   * Returns array of violations found
   */
  static async checkContentViolations(
    content: string,
    filterType: FilterType
  ): Promise<{ violated: boolean; violations: FilterSetting[] }> {
    try {
      const activeFilters = await this.getActiveFiltersForType(filterType);
      const violations: FilterSetting[] = [];

      for (const filter of activeFilters) {
        const keyword = filter.keyword.trim();
        if (!keyword) continue;

        let matches = false;
        const searchText = filter.case_sensitive ? content : content.toLowerCase();
        const searchKeyword = filter.case_sensitive ? keyword : keyword.toLowerCase();

        // Simple substring matching (can be enhanced with regex support later)
        if (searchText.includes(searchKeyword)) {
          matches = true;
        }

        if (matches) {
          violations.push(filter);
        }
      }

      return {
        violated: violations.length > 0,
        violations,
      };
    } catch (error) {
      console.error('Error checking content violations:', error);
      // On error, don't block content - fail open
      return { violated: false, violations: [] };
    }
  }

  /**
   * Check multiple content fields at once
   */
  static async checkMultipleContentViolations(
    contents: { type: FilterType; value: string }[]
  ): Promise<{ violated: boolean; violations: { type: FilterType; violations: FilterSetting[] }[] }> {
    try {
      const allViolations: { type: FilterType; violations: FilterSetting[] }[] = [];
      let hasViolations = false;

      for (const content of contents) {
        const result = await this.checkContentViolations(content.value, content.type);
        if (result.violated) {
          hasViolations = true;
          allViolations.push({
            type: content.type,
            violations: result.violations,
          });
        }
      }

      return {
        violated: hasViolations,
        violations: allViolations,
      };
    } catch (error) {
      console.error('Error checking multiple content violations:', error);
      return { violated: false, violations: [] };
    }
  }
}

